import SearchBar from "@/ui-components/search-bar";
import { useSearchStocksRatings } from "./hooks/useSearchStocksRatings";
import { FirehoseHighlightItem } from "../firehose-all/components/FirehoseHighlightItem";
import React, { useEffect, useMemo, useState } from "react";
import { GroupedVirtuoso } from "react-virtuoso";
import { NewsFeedItem } from "@/shared/api/firehose/models";
import toast from "react-hot-toast";
import * as Sentry from "@sentry/react";
import { CommonSearchProps } from "@/widget-components/newsquawk-headlines/HeadlinesSearchForm";
import { LoadMoreButton } from "@/ui-components/load-more-button";
import EmptyPlaceholder from "@/ui-components/empty-placeholder";
import { DateSectionHeader } from "../shared/DateSectionHeader";
import { useWidgetSearchState } from "@/hooks/useWidgetSearchState";

function groupFirehoseByDate(items: NewsFeedItem[]) {
  const grouped = items.reduce(
    (acc, item) => {
      const date = new Date(item.created_utc);
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);

      let dateKey: string;
      if (date.toDateString() === today.toDateString()) {
        dateKey = "Today";
      } else if (date.toDateString() === yesterday.toDateString()) {
        dateKey = "Yesterday";
      } else {
        dateKey = date.toLocaleDateString("en-US", {
          weekday: "long",
          month: "short",
          day: "numeric",
        });
      }

      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(item);
      return acc;
    },
    {} as Record<string, NewsFeedItem[]>
  );

  const groups = Object.keys(grouped).sort((a, b) => {
    if (a === "Today") return -1;
    if (b === "Today") return 1;
    if (a === "Yesterday") return -1;
    if (b === "Yesterday") return 1;
    return new Date(b).getTime() - new Date(a).getTime();
  });

  const groupedCounts = groups.map((group) => grouped[group].length);
  const flatItems = groups.flatMap((group) => grouped[group]);

  return { groups, groupedCounts, items: flatItems };
}

function StocksRatingsSearchForm(props: CommonSearchProps) {
  const {
    isLoading,
    isSuccess,
    isError,
    searchStocksRatingsData,
    searchResults,
    clearSearchResults,
    loadNextPage,
    hasMore,
  } = useSearchStocksRatings();

  const { groups, groupedCounts, items } = useMemo(
    () => groupFirehoseByDate(searchResults),
    [searchResults]
  );

  const { setTitle, formOpen = true, persistKey, onActiveChange } = props;
  const [savedSearch, setSavedSearch] = useWidgetSearchState(persistKey);

  const [query, setQuery] = useState<string>(savedSearch.query ?? "");

  const [startDate, setStartDate] = React.useState<string>(
    savedSearch.startDate ?? ""
  );
  const [startTime, setStartTime] = React.useState<string>(
    savedSearch.startTime ?? "00:00"
  );
  const [endDate, setEndDate] = React.useState<string>(
    savedSearch.endDate ?? ""
  );
  const [endTime, setEndTime] = React.useState<string>(
    savedSearch.endTime ?? "23:59"
  );

  const formRef = React.useRef<HTMLDivElement>(null);

  const [formHeight, setFormHeight] = React.useState<number>(40);

  const showSearchResults = searchResults.length > 0 || isSuccess;

  // Runs the search from current state. No store write — used by the Search
  // button/Enter (via handleSearch) and by the on-mount re-run of a saved search.
  const runSearch = (q: string) => {
    // Require at least a text query or a date range before searching.
    if (!q.trim() && !startDate && !endDate) return;

    clearSearchResults();

    // Convert date strings to ISO strings with time
    let startDateISO: string | undefined;
    let endDateISO: string | undefined;

    if (startDate) {
      const [h, m] = (startTime || "00:00").split(":").map(Number);
      const startDateObj = new Date(startDate);
      startDateObj.setHours(h, m, 0, 0);
      startDateISO = startDateObj.toISOString();
    }

    if (endDate) {
      const [h, m] = (endTime || "23:59").split(":").map(Number);
      const endDateObj = new Date(endDate);
      endDateObj.setHours(h, m, 59, 999);
      endDateISO = endDateObj.toISOString();
    }

    searchStocksRatingsData({
      q: q,
      limit: 50,
      date_range: { start_date: startDateISO, end_date: endDateISO },
    });
  };

  const handleSearch = (q: string) => {
    // Require at least a text query or a date range before searching, so an
    // empty Enter / Search press with nothing entered is a no-op.
    if (!q.trim() && !startDate && !endDate) return;

    runSearch(q);

    // Persist params so the search stays in play after Done and across reloads.
    setSavedSearch({ query: q, startDate, startTime, endDate, endTime });
  };

  const handleClear = () => {
    setQuery("");
    setStartDate("");
    setEndDate("");
    clearSearchResults();
    setSavedSearch({});
  };

  const footer = hasMore ? (
    <LoadMoreButton loading={isLoading} loadNextPage={loadNextPage} />
  ) : (
    <div className="flex justify-center py-4 text-gray-400 text-sm">
      End of results
    </div>
  );

  useEffect(() => {
    if (!isError) return;
    toast.error(
      "There was an error fetching the stocks ratings stories, please try again."
    );
    Sentry.captureMessage("Error fetching stocks ratings stories in search");
  }, [isError]);

  useEffect(() => {
    const titleString = query
      ? `Showing results for '${query}'`
      : "Showing search results";

    if (showSearchResults) {
      setTitle?.(titleString);
    } else {
      setTitle?.("Broker Ratings");
    }

    return () => setTitle?.("Broker Ratings");
  }, [showSearchResults, query, setTitle]);

  // Re-apply a persisted search on mount (after Done / tab switch / reload).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      savedSearch.query?.trim() ||
      savedSearch.startDate ||
      savedSearch.endDate
    ) {
      runSearch(savedSearch.query ?? "");
    }
  }, []);

  // Tell the container whether a search is in play, so it keeps this component
  // mounted after Done (results persist) and drops it once cleared.
  useEffect(() => {
    onActiveChange?.(showSearchResults || isSuccess);
  }, [showSearchResults, isSuccess, onActiveChange]);

  // Clear search results when the component unmounts
  useEffect(() => clearSearchResults, [clearSearchResults]);

  // Measure and update form height dynamically
  useEffect(() => {
    const updateFormHeight = () => {
      if (formRef.current) {
        const height = formRef.current.offsetHeight;
        setFormHeight(height);
      }
    };

    // Initial measurement
    updateFormHeight();

    // Set up ResizeObserver to handle dynamic height changes
    const resizeObserver = new ResizeObserver(updateFormHeight);
    if (formRef.current) {
      resizeObserver.observe(formRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <React.Fragment>
      <div
        className={`bg-gray-800 border-b border-gray-700/50 px-3 py-2 space-y-2${
          formOpen ? "" : " hidden"
        }`}
        ref={formRef}
      >
        {/* Search bar — auto-submits as you type; the action row below adds explicit Search/Clear/Done. */}
        <SearchBar
          value={query}
          onChange={setQuery}
          loading={isLoading}
          onSearch={handleSearch}
          onClear={handleClear}
        />

        {/* Date/time range + actions. Sticky so they stay reachable on a short widget. */}
        <div className="sticky bottom-0 z-10 bg-gray-800 flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">From:</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
            />
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
            />
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">To:</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
            />
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
            />
          </div>

          <div className="ml-auto flex items-center gap-2">
            {(query || startDate || endDate || showSearchResults) && (
              <button
                type="button"
                onClick={handleClear}
                className="px-3 py-1 text-xs font-medium rounded text-gray-300 hover:bg-gray-700 transition-colors"
              >
                Clear
              </button>
            )}
            <button
              type="button"
              onClick={() => handleSearch(query)}
              disabled={!query.trim() && !startDate && !endDate}
              className="px-3 py-1 text-xs font-medium rounded bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Search
            </button>
            {props.onClose && (
              <button
                onClick={props.onClose}
                className="px-3 py-1 text-xs font-medium rounded bg-squawk-blue text-white hover:bg-squawk-blue/80 transition-colors"
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>

      {showSearchResults ? (
        <section
          className="absolute flex-1 z-30 bg-gray-900 inset-0 pb-[22px] overflow-y-auto scrollbar-thin"
          style={{ top: formOpen ? `${formHeight}px` : 0 }}
        >
          <GroupedVirtuoso
            className="scrollbar-thin absolute inset-x-0 top-0"
            groupCounts={groupedCounts}
            groupContent={(index) =>
              groups[index] === "Today" ? (
                <p className="h-[1px]" />
              ) : (
                <DateSectionHeader title={groups[index]} />
              )
            }
            itemContent={(idx) => (
              <FirehoseHighlightItem key={items[idx].id} item={items[idx]} />
            )}
            components={{
              Footer: () => footer,
            }}
          />
        </section>
      ) : query && !isLoading ? (
        <section
          className="absolute flex-1 z-30 bg-gray-900 inset-0 pb-[40px] overflow-y-auto scrollbar-thin"
          style={{ top: formOpen ? `${formHeight}px` : 0 }}
        >
          <EmptyPlaceholder
            message={"No stocks ratings stories found"}
            isLoading={false}
          />
        </section>
      ) : null}
    </React.Fragment>
  );
}

export default StocksRatingsSearchForm;
